import { getBlocklyWorkspace } from "../blocks";
import { ReplayEventAdapter } from "./replayEventAdapter";

const BASE_STEP_MS = 1000;

export default class ReplayEngine {
    constructor(timeline, replayControls, vm, baselineProjectJson = null) {
        this.timeline = timeline;
        this.controls = replayControls;
        this.vm = vm;
        this.adapter = new ReplayEventAdapter(vm);
        this.baselineProjectJson = baselineProjectJson;

        this.index = -1;
        this.isPlaying = false;
        this.playbackRate = 1;
        this._timer = null;

        this.enabledTypes = new Set(timeline.map((evt) => evt.eventType));
        this.stops = [];
        this._updateStops();

        this.controls.onPrev = () => this.prev();
        this.controls.onNext = () => this.next();
        this.controls.onPlayPause = (playing) => this.togglePlay(playing);
        this.controls.onSpeedChange = (rate) => this.setPlaybackRate(rate);
        this.controls.onFilterChange = (types) => this.setFilter(types);
    }

    start() {
        this.index = -1;
        this.controls.setEventTypes(this.getEventTypes());
        this.controls.show();
        this._render();
    }

    getEventTypes() {
        const types = new Map();
        this.timeline.forEach(({ eventType, label }) => {
            if (!types.has(eventType))
                types.set(eventType, { type: eventType, label, count: 0 });
            types.get(eventType).count++;
        });
        return Array.from(types.values());
    }

    async next() {
        const nextStop = this.stops.find((i) => i > this.index);
        if (nextStop === undefined) {
            if (this.isPlaying) this.controls.togglePlayPause();
            return false;
        }

        for (let i = this.index + 1; i <= nextStop; i++) {
            await this._apply(this.timeline[i], i === nextStop);
        }
        this.index = nextStop;
        this._render();

        return true;
    }

    async prev() {
        if (this.index < 0) return false;

        const prevStop = this._lastStopBefore(this.index);
        if (prevStop === undefined) {
            this.index = -1;
            await this._clearWorkspace();
        } else {
            await this._rebuildTo(prevStop);
        }

        this._render();
        return true;
    }

    togglePlay(playing) {
        this.isPlaying = playing;
        this._restartTimer();
    }

    setPlaybackRate(rate) {
        this.playbackRate = rate;
        this._restartTimer();
    }

    setFilter(types) {
        this.enabledTypes = new Set(types);
        this._updateStops();
        this._render();
    }

    _restartTimer() {
        clearTimeout(this._timer);
        this._timer = null;
        this._playToken = (this._playToken || 0) + 1;

        if (this.isPlaying) this._scheduleTick();
    }

    _scheduleTick() {
        const token = this._playToken;

        this._timer = setTimeout(async () => {
            if (token !== this._playToken) return;

            const advanced = await this.next();

            if (token !== this._playToken) return;
            if (advanced && this.isPlaying) this._scheduleTick();
        }, BASE_STEP_MS / this.playbackRate);
    }

    _updateStops() {
        this.stops = [];
        this.timeline.forEach((evt, i) => {
            if (this.enabledTypes.has(evt.eventType)) this.stops.push(i);
        });
    }

    _lastStopBefore(index) {
        for (let s = this.stops.length - 1; s >= 0; s--) {
            if (this.stops[s] < index) return this.stops[s];
        }
        return undefined;
    }

    _render() {
        const position = this.stops.filter((i) => i <= this.index).length;
        this.controls.updateProgress(position, this.stops.length);

        const evt = this.timeline[this.index];
        if (!evt) {
            let status = "Esperando...";
            if (this.timeline.length === 0) status = "Sin eventos";
            else if (this.stops.length === 0)
                status = "Sin eventos seleccionados";

            this.controls.updateStepInfo({
                timestamp: "--/--/-- --:--:--",
                eventType: status,
                author: "---",
            });
            return;
        }

        this.controls.updateStepInfo({
            timestamp: evt.timestamp,
            eventType: evt.label,
            author: evt.author,
        });
    }

    async _rebuildTo(targetIdx) {
        await this._clearWorkspace();

        for (let i = 0; i <= targetIdx; i++) {
            await this._apply(this.timeline[i], false);
        }
        this.index = targetIdx;
    }

    async _apply(evt, animate = true) {
        const action = this.adapter.adapt(evt);

        if (typeof action !== "function") {
            console.log("[Replay] Skipped (no action)");
            return;
        }

        await action(animate, this.playbackRate);
    }

    async _clearWorkspace() {
        if (this.baselineProjectJson) {
            try {
                await this.vm.loadProject(this.baselineProjectJson);
                this.adapter = new ReplayEventAdapter(this.vm);
                return;
            } catch (e) {
                console.error(
                    "[Replay] Error reloading baseline project, falling back to clearing the visible target only:",
                    e,
                );
            }
        }

        this.adapter.resetToBaseline();

        const ws = getBlocklyWorkspace();
        ws.getTopBlocks(false).forEach((block) => block.dispose(false));
    }
}
